import React, { useRef, useState } from 'react'
import moment from 'moment'
import { FREQUENCIES, ENDS, END_PICKS, endAfter, plannedVisits, repeatSaid, endIsValid, dateSaid, checkSummary, choiceFor, minutesSaid } from './repeat'
import ShiftTimePicker from '../../Diary/ShiftTimePicker'
import StaffPicker from '../../Diary/StaffPicker'
import DiaryDatePicker from '../../Diary/DiaryDatePicker'
import './Repeat.css'

// The "Repeat" row in the new-booking panel: how often, and until when. Off
// ("Doesn't repeat") it's one quiet line, so a normal booking is unchanged.
// Same pickers as the rest of the panel (StaffPicker's dropdown/sheet and
// DiaryDatePicker), so it looks and behaves like Staff, Date and Time.
const asOptions = (list) => list.map(x => ({ id: x.id, name: x.label }))

export const RepeatControl = ({ value, onChange, firstDate }) => {
  const v = value || { freq: 'none', end: 'never', endDate: '', endCount: 6 }
  const set = (patch) => onChange({ ...v, ...patch })
  const on = v.freq !== 'none'
  const [dateOpen, setDateOpen] = useState(false)
  const anchor = useRef(null)
  const ok = endIsValid({ ...v, firstDate })
  return (
    <>
      <div className="bdp-edit-field rpt-field rpt-first">
        <label className="bdp-edit-label">Repeat</label>
        <StaffPicker label="Repeat" searchable={false} staffList={asOptions(FREQUENCIES)} value={v.freq} onChange={freq => set({ freq })} />
      </div>
      {on && (
        <div className="bdp-edit-field rpt-field">
          <label className="bdp-edit-label">Ends</label>
          <StaffPicker label="Ends" searchable={false} staffList={asOptions(ENDS)} value={v.end} onChange={end => set({ end })} />
          {v.end === 'date' && (
            <div className="rpt-until rpt-until--date">
              <div
                className="bdp-edit-input rpt-input"
                ref={anchor}
                role="button"
                tabIndex={0}
                onClick={() => setDateOpen(true)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setDateOpen(true) }}
                style={!v.endDate ? { color: '#999' } : undefined}
              >
                {v.endDate ? moment(v.endDate).format('ddd D MMM YYYY') : 'Last date'}
              </div>
              <div className="rpt-picks" role="group" aria-label="Quick end dates">
                {firstDate && END_PICKS.map(p => {
                  const d = endAfter(firstDate, p.months)
                  return (
                    <button key={p.months} type="button" className={`rpt-chip${v.endDate === d ? ' is-on' : ''}`} aria-pressed={v.endDate === d} onClick={() => set({ endDate: d })}>{p.label}</button>
                  )
                })}
              </div>
              <DiaryDatePicker
                open={dateOpen}
                selected={v.endDate ? moment(v.endDate).toDate() : (firstDate ? moment(firstDate).add(3, 'months').toDate() : new Date())}
                anchorEl={anchor.current}
                onChange={(d) => { set({ endDate: moment(d).format('YYYY-MM-DD') }); setDateOpen(false) }}
                onClose={() => setDateOpen(false)}
              />
            </div>
          )}
          {v.end === 'count' && (
            <div className="rpt-until rpt-count-row">
              <button type="button" className="rpt-step" aria-label="Fewer visits" onClick={() => set({ endCount: Math.max(2, (Number(v.endCount) || 2) - 1) })}>−</button>
              <span className="bdp-edit-input rpt-count" aria-live="polite">{Number(v.endCount) || 2} visits</span>
              <button type="button" className="rpt-step" aria-label="More visits" onClick={() => set({ endCount: Math.min(520, (Number(v.endCount) || 2) + 1) })}>+</button>
            </div>
          )}
          <p className={`rpt-said${ok ? '' : ' is-bad'}`}>
            {!ok
              ? (v.end === 'date' ? 'Choose a last date after the first visit.' : 'Choose 2 visits or more.')
              : v.end === 'never'
                ? <>{repeatSaid({ ...v, firstDate })}. You’ll get a reminder before it runs out. <button type="button" className="rpt-link" onClick={() => { set({ end: 'date', endDate: firstDate ? endAfter(firstDate, 24) : '' }) }}>Book further ahead</button></>
                : `${repeatSaid({ ...v, firstDate })}${v.end === 'date' && plannedVisits({ ...v, firstDate }) ? `, ${plannedVisits({ ...v, firstDate })} visits` : ''}.`}
          </p>
        </div>
      )}
    </>
  )
}

// Check the dates: when any date overlaps another booking or can't be
// booked, every visit is listed by month before anything is booked. Each
// problem says why and offers its choices: an overlap is booked anyway
// unless they skip or move it, a date that can't be booked is skipped
// unless they move it. The button counts what will really be booked.
const WORDS = { keep: 'Book anyway', skip: 'Skip', bulkKeep: 'Book all anyway', bulkSkip: 'Skip all' }
export const DateCheck = ({ dates, choices, times = {}, onChoose, onChooseAll, onTime, onView, onChangeTime, timeLabel, startMinutes, flashDate, words: given }) => {
  const words = { ...WORDS, ...(given || {}) }
  const list = dates || []
  const sum = checkSummary(list)
  const months = []
  list.forEach((d) => {
    const key = moment(d.date, 'YYYY-MM-DD').format('MMMM YYYY')
    const last = months[months.length - 1]
    if (last && last.key === key) last.dates.push(d)
    else months.push({ key, dates: [d] })
  })
  // The server's reason as a sentence ("overlaps 2 other bookings")
  const why = r => (r ? r.charAt(0).toUpperCase() + r.slice(1) : '')
  // Several of a kind (a weekly clash): one tap for all of them
  const all = (status, how) => list.filter(d => d.status === status).every(d => choiceFor(d, choices, times) === how)
  const kinds = [sum.ok, sum.overlap, sum.blocked].filter(n => n > 0).length
  const bulkOverlap = onChooseAll && sum.overlap >= 3
  // The server now suggests a free time for an overlap too
  const overlapMoves = list.some(d => d.status === 'overlap' && d.suggestion)
  const bulkBlocked = onChooseAll && sum.blocked >= 3 && list.some(d => d.status === 'blocked' && d.suggestion)
  const asDate = (m) => { const x = new Date(); x.setHours(Math.floor(m / 60), m % 60, 0, 0); return x }
  // What the row's date line says about its time
  const when = (d) => {
    const c = choiceFor(d, choices, times)
    if (c === 'move' && d.suggestion) return `, moved to ${d.suggestion.at}`
    if (c === 'custom' && times[d.date] != null) return `, moved to ${minutesSaid(times[d.date])}`
    return timeLabel ? `, ${timeLabel}` : ''
  }
  const bulk = (status, how, label) => (
    <button type="button" className={`rpt-chip${all(status, how) ? ' is-on' : ''}`} aria-pressed={all(status, how)} onClick={() => onChooseAll(status, how)}>{label}</button>
  )
  // Change time: on the diary when it's open (drag the visit), else a picker
  const changeChip = d => (onChangeTime ? (
    <button
      type="button"
      className={`rpt-chip${choiceFor(d, choices, times) === 'custom' ? ' is-on' : ''}`}
      aria-pressed={choiceFor(d, choices, times) === 'custom'}
      onClick={() => onChangeTime(d)}
    >{times[d.date] != null ? 'Change again' : 'Change time'}</button>
  ) : onTime ? chip(d, 'custom', 'Change time') : null)
  const chip = (d, how, label) => (
    <button
      type="button"
      className={`rpt-chip${choiceFor(d, choices, times) === how ? ' is-on' : ''}`}
      aria-pressed={choiceFor(d, choices, times) === how}
      onClick={() => onChoose(d.date, how)}
    >{label}</button>
  )
  return (
    <div className="rpt-check">
      {/* With one kind of problem the bulk row below says it already */}
      {(kinds > 1 || !(bulkOverlap || bulkBlocked)) && <div className="rpt-sum" aria-label="What the check found">
        {sum.ok > 0 && <span className="rpt-pill is-ok">{sum.ok} free</span>}
        {sum.overlap > 0 && <span className="rpt-pill is-warn">{sum.overlap} overlap{sum.overlap === 1 ? 's' : ''} another booking</span>}
        {sum.blocked > 0 && <span className="rpt-pill is-bad">{sum.blocked} can’t be booked</span>}
      </div>}
      {bulkOverlap && (
        <div className="rpt-bulk">
          <span>{sum.overlap} {sum.overlap === 1 ? 'date overlaps' : 'dates overlap'} another booking</span>
          <span className="rpt-visit-acts">{bulk('overlap', 'keep', words.bulkKeep)}{overlapMoves && bulk('overlap', 'move', 'Move all where possible')}{bulk('overlap', 'skip', words.bulkSkip)}</span>
        </div>
      )}
      {bulkBlocked && (
        <div className="rpt-bulk">
          <span>{sum.blocked} dates can’t be booked</span>
          <span className="rpt-visit-acts">{bulk('blocked', 'skip', words.bulkSkip)}{bulk('blocked', 'move', 'Move all where possible')}</span>
        </div>
      )}
      <div className="rpt-visits">
        {months.map(m => (
          <div key={m.key} className="rpt-month">
            <p className="rpt-month-name">{m.key}</p>
            <ul>
              {m.dates.map(d => (
                <li key={d.date} data-date={d.date} className={`rpt-visit is-${d.status}${d.status !== 'ok' && choiceFor(d, choices, times) === 'skip' ? ' is-skipped' : ''}${flashDate === d.date ? ' is-flash' : ''}`}>
                  <span className="rpt-visit-ic" aria-hidden="true">{d.status === 'ok' ? '✓' : d.status === 'overlap' ? '!' : '✕'}</span>
                  <span className="rpt-visit-main">
                    <span className="rpt-visit-date">
                      {dateSaid(d.date)}{when(d)}
                    </span>
                    {d.status !== 'ok' && d.reason && <span className="rpt-visit-why">{why(d.reason)}</span>}
                    {d.status === 'overlap' && (
                      <span className="rpt-visit-acts">
                        {chip(d, 'keep', words.keep)}
                        {d.suggestion && chip(d, 'move', `Move to ${d.suggestion.at}`)}
                        {changeChip(d)}
                        {chip(d, 'skip', words.skip)}
                      </span>
                    )}
                    {d.status === 'blocked' && (
                      <span className="rpt-visit-acts">
                        {chip(d, 'skip', words.skip)}
                        {d.suggestion && chip(d, 'move', `Move to ${d.suggestion.at}`)}
                        {changeChip(d)}
                      </span>
                    )}
                    {d.status !== 'ok' && onTime && !onChangeTime && choiceFor(d, choices, times) === 'custom' && (
                      <span className="rpt-visit-time">
                        <ShiftTimePicker
                          label={`Time on ${dateSaid(d.date)}`}
                          selected={asDate(times[d.date] != null ? times[d.date] : (startMinutes || 540))}
                          placeholder="Pick a time"
                          onChange={(t) => onTime(d.date, t.getHours() * 60 + t.getMinutes())}
                        />
                        {times[d.date] == null && <span className="rpt-visit-why">Pick a time for this visit. Only this one moves.</span>}
                      </span>
                    )}
                    {d.status !== 'ok' && onView && (
                      <button type="button" className="rpt-view" onClick={() => onView(d)}>See it in the diary</button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

// Which visits a change or a cancel applies to: option rows, the words
// beside the radio and a line under them saying what that means
export const ScopeOptions = ({ name, scopes, value, onChange, labelFor, detailFor, disabled }) => (
  <div className="rpt-opts" role="radiogroup" aria-label="Which visits">
    {scopes.map(scope => (
      <button
        key={scope}
        type="button"
        role="radio"
        aria-checked={value === scope}
        className={`rpt-opt${value === scope ? ' is-on' : ''}`}
        onClick={() => !disabled && onChange(scope)}
        name={name}
      >
        <span className="rpt-radio" aria-hidden="true" />
        <span className="rpt-opt-words">
          <b>{labelFor(scope)}</b>
          {detailFor(scope) && <span>{detailFor(scope)}</span>}
        </span>
      </button>
    ))}
  </div>
)

// Editing a visit of a repeat: this visit, or this and the later ones
export const ScopeSwitch = ({ value, onChange, laterLabel, line }) => (
  <div className="rpt-scope">
    <p className="rpt-scope-title">Apply changes to</p>
    <div className="rpt-seg" role="radiogroup" aria-label="Apply changes to">
      <button type="button" role="radio" aria-checked={value !== 'following'} className={value !== 'following' ? 'is-on' : ''} onClick={() => onChange('this')}>This visit</button>
      <button type="button" role="radio" aria-checked={value === 'following'} className={value === 'following' ? 'is-on' : ''} onClick={() => onChange('following')}>{laterLabel}</button>
    </div>
    <p className="rpt-scope-line">{line}</p>
  </div>
)

export default RepeatControl
